import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

/** Loading / error / empty states shared by the report views. */
export function QueryState({
  isLoading,
  isError,
  isEmpty,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  isEmpty?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <p className="py-8 text-center text-muted-foreground">
        {t("reports.state.loading")}
      </p>
    );
  }
  if (isError) {
    return (
      <p className="py-8 text-center text-destructive">
        {t("reports.state.error")}
      </p>
    );
  }
  if (isEmpty) {
    return (
      <p className="py-8 text-center text-muted-foreground">
        {t("reports.state.empty")}
      </p>
    );
  }
  return <>{children}</>;
}

/** Prev/next pager over a DRF-paginated list. */
export function PaginationBar({
  page,
  count,
  pageSize,
  onPage,
}: {
  page: number;
  count: number;
  pageSize: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  if (count === 0) return null;
  return (
    <div className="flex items-center justify-between gap-4 pt-2">
      <span className="text-sm text-muted-foreground">
        {t("reports.state.results", { count })}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          {t("reports.state.previous")}
        </Button>
        <span className="text-sm text-muted-foreground">
          {t("reports.state.page", { page })}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          {t("reports.state.next")}
        </Button>
      </div>
    </div>
  );
}
